import React, { useState } from 'react';
import Block from './Block';
import PillTag from './PillTag';
import BarChart from './BarChart';
import Icon from '../primitives/Icon';
import { Button } from '../../components/ui/button';
import { formatMetricNumber, metricMeta, metricTone } from './metricTone';

/**
 * Renders a `model_performance` block from a PreliminaryModelResult /
 * FullModelResult payload. The backend will hand us:
 *   { target_column, model_type, task_type, train_metric, test_metric,
 *     train_metric_name, test_metric_name, training_time_sec,
 *     model_kind: "preliminary_model" | "full_model",
 *     n_missing_target: number | null, ... }
 */

// Scale, tier, label and tooltip for the metric tiles all come from the shared
// registry in metricTone.js, so this block, the comparison cards and the
// artifacts rail cannot disagree about what e.g. "rmse" means.
//   Higher-is-better ratios (accuracy, ROC AUC, R², F1): red < 50%, yellow 50–75%, green > 75%
//   MAPE (lower is better — bounds from industry forecasting practice):
//     green < 15%, yellow 15–30%, red > 30%
//   Absolute errors and unbounded scores (RMSE, MAE, log loss, QWK): neutral, no
//     universal threshold to tier them against.
const TIER_STYLE = {
  green:   { background: 'var(--green-soft)', border: 'var(--border)', text: 'var(--green)' },
  warn:    { background: 'var(--warn-50)', border: 'var(--border)', text: 'var(--warn-500)' },
  error:   { background: 'var(--error-50)', border: 'var(--border)', text: 'var(--error-500)' },
  neutral: { background: 'var(--muted)', border: 'var(--border)', text: 'var(--foreground)' },
};

const ModelPerformance = ({ blockData = {}, onTrainFullModel }) => {
  const test = Number(blockData.test_metric ?? 0);
  const train = Number(blockData.train_metric ?? 0);
  const isClassifier = (blockData.task_type || '').toLowerCase() === 'classification';
  const testMetricName = blockData.test_metric_name || (isClassifier ? 'accuracy' : 'score');
  const trainMetricName = blockData.train_metric_name || testMetricName;
  const featureImportance = (blockData.feature_importance || []).slice(0, 8);
  const duration = blockData.training_time_sec
    ? `${Number(blockData.training_time_sec).toFixed(1)}s`
    : null;
  const isPreliminary = blockData.model_kind === 'preliminary_model';
  const targetColumn = blockData.target_column || 'target';
  const showFullCTA = isPreliminary && !!onTrainFullModel;
  const nMissing = blockData.n_missing_target;

  const testTierStyle = TIER_STYLE[metricTone(test, testMetricName)] || TIER_STYLE.neutral;

  return (
    <Block
      icon="brain"
      title={`Model · ${targetColumn}`}
      badge={
        <div style={{ display: 'flex', gap: 6 }}>
          {duration && <PillTag tone="green" dot>Trained in {duration}</PillTag>}
          {blockData.model_type && <PillTag tone="neutral">{blockData.model_type}</PillTag>}
          {isPreliminary && <PillTag tone="accent">Preliminary</PillTag>}
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: featureImportance.length ? 18 : 0 }}>
        <MetricTile
          value={formatMetricNumber(test, testMetricName)}
          label={`Test ${metricMeta(testMetricName).label}`}
          tooltip={metricMeta(testMetricName).tooltip}
          tierStyle={testTierStyle}
        />
        <MetricTile
          value={formatMetricNumber(train, trainMetricName)}
          label={`Train ${metricMeta(trainMetricName).label}`}
          tooltip={metricMeta(trainMetricName).tooltip}
          tierStyle={TIER_STYLE.neutral}
        />
      </div>
      {featureImportance.length > 0 && (
        <BarChart
          title="Top features driving the prediction"
          data={featureImportance}
          valueFormat={(v) => v.toFixed(2)}
        />
      )}
      {blockData.summary && (
        <div style={{
          marginTop: 14, padding: '10px 12px',
          background: 'var(--secondary)', border: '1px solid var(--border)',
          borderRadius: 4, fontSize: 12.5, lineHeight: 1.5, color: 'var(--fg)',
        }}>
          <strong style={{ color: 'var(--foreground)' }}>What this means: </strong>{blockData.summary}
        </div>
      )}
      {showFullCTA && (
        <TrainFullCTA
          target={targetColumn}
          nMissing={nMissing}
          onClick={() => onTrainFullModel(targetColumn)}
        />
      )}
    </Block>
  );
};

const MetricTile = ({ value, label, tooltip, tierStyle }) => {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        padding: '12px 14px',
        background: tierStyle.background,
        borderRadius: 'var(--r-md)',
        border: `1px solid ${tierStyle.border}`,
        cursor: tooltip ? 'help' : 'default',
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <div style={{
        fontSize: 32, fontWeight: 600, color: tierStyle.text,
        letterSpacing: '-0.02em', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 10.5, color: 'var(--fg-subtle)', textTransform: 'uppercase',
        letterSpacing: '0.1em', marginTop: 6, fontWeight: 500,
      }}>
        {label}
      </div>
      {tooltip && show && (
        <div role="tooltip" style={{
          position: 'absolute', zIndex: 5,
          left: 0, right: 0, top: 'calc(100% + 6px)',
          padding: '8px 10px',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          fontSize: 11.5, lineHeight: 1.45, color: 'var(--foreground)',
          pointerEvents: 'none',
        }}>
          {tooltip}
        </div>
      )}
    </div>
  );
};

const TrainFullCTA = ({ target, nMissing, onClick }) => {
  const knowsCount = typeof nMissing === 'number';
  const targetSpan = <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--muted-foreground)' }}>{target}</span>;
  const subtitle = knowsCount
    ? <>Auto Predicting <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--muted-foreground)' }}>{nMissing.toLocaleString()}</span> {nMissing === 1 ? 'value' : 'values'} for {targetSpan}.</>
    : <>Final metrics for {targetSpan}.</>;
  return (
    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <Button onClick={onClick}>
        <Icon name="zap" size={13} />
        Train full model
        <Icon name="arrow-right" size={13} />
      </Button>
      <span style={{ fontSize: 11.5, color: 'var(--muted-foreground)', lineHeight: 1.4 }}>
        {subtitle}
      </span>
    </div>
  );
};

export default ModelPerformance;
